import React from 'react';

import { BlockableComponent, BlockableComponents, Enums, Models } from '@components/SurveyBuilder';

interface Props {
  block: Models.Block;
  value: any;
  onChange: (...event: any[]) => void;
  debouncedChange: (...event: any[]) => void;
}

export const BlockableComponentRenderer: React.FC<Props> = ({ block, onChange, value, debouncedChange }) => {
  const { kind } = block;

  switch (kind) {
    case Enums.Kind.linearScale:
      return (
        <BlockableComponent<BlockableComponents.LinearScale>
          kind={kind}
          componentProps={{
            onChangeOptions: (options) => debouncedChange({ ...value, options }),
            options: value.options,
            isEditable: true
          }}
        />
      );

    case Enums.Kind.multiSelect:
      return (
        <BlockableComponent<BlockableComponents.MultiSelect>
          kind={kind}
          componentProps={{
            onChangeOptions: (options) => {
              debouncedChange({ ...value, options });
            },
            onChangeOther: (other) => {
              onChange({ ...value, other });
            },
            onChangeNoneOfAbove: (none_of_above) => {
              onChange({ ...value, none_of_above });
            },
            options: value?.options,
            isEditable: true,
            other: value?.other,
            none_of_above: value?.none_of_above
          }}
        />
      );
    case Enums.Kind.singleSelect:
      return (
        <BlockableComponent<BlockableComponents.SingleSelect>
          kind={kind}
          componentProps={{
            onChangeOptions: (options) => {
              debouncedChange({ ...value, options });
            },
            onChangeOther: (other) => {
              onChange({ ...value, other });
            },
            options: value?.options,
            isEditable: true,
            other: value?.other
          }}
        />
      );

    case Enums.Kind.prototypeTest:
      return (
        <BlockableComponent<BlockableComponents.PrototypeTest>
          kind={kind}
          componentProps={{
            block: { ...block, blockable: value },
            url: value?.url,
            title: value?.title,
            paths: value?.paths,
            settings: value?.settings,
            onChange: (data) => {
              const { settings: prevSettings, ...blockableWithoutSettings } = value ?? {};

              const settingsChanged = Object.keys(data?.settings || {}).length > 0;

              const previousValue = settingsChanged ? { ...value } : { ...blockableWithoutSettings };

              onChange({
                ...previousValue,
                ...data,
                ...(settingsChanged && { settings: { ...prevSettings, ...data!.settings } })
              });
            }
          }}
        />
      );

    case Enums.Kind.permissions:
      return (
        <BlockableComponent<BlockableComponents.Permissions>
          kind={kind}
          componentProps={{
            camera: value?.camera,
            microphone: value?.microphone,
            screen: value?.screen,
            settings: value?.settings,
            onChange: (data) => {
              onChange({ ...value, ...data });
            }
          }}
        />
      );

    case Enums.Kind.yesNo:
      return <BlockableComponent<BlockableComponents.YesNo> kind={kind} componentProps={{ options: value?.options }} />;

    case Enums.Kind.websiteTest:
      return (
        <BlockableComponent<BlockableComponents.WebsiteTest>
          kind={kind}
          componentProps={{
            block: { ...block, blockable: value },
            url: value?.url,
            settings: value?.settings,
            onChange: (data) => {
              debouncedChange({ ...value, ...data });
            }
          }}
        />
      );

    case Enums.Kind.cardSort:
      return (
        <BlockableComponent<BlockableComponents.CardSort>
          kind={kind}
          componentProps={{
            sort_type: value?.sort_type,
            cards: value?.cards,
            categories: value?.categories,
            require_all_sorted: value?.require_all_sorted,
            randomise_cards: value?.randomise_cards,
            randomise_categories: value?.randomise_categories,
            debouncedChange: (data) => {
              debouncedChange({ ...value, ...data });
            },
            onChange: (data) => {
              onChange({ ...value, ...data });
            }
          }}
        />
      );

    case Enums.Kind.treeTest:
      return (
        <BlockableComponent<BlockableComponents.TreeTest>
          kind={kind}
          componentProps={{
            block: { ...block, blockable: value },
            nodes: value?.nodes ?? [],
            randomise_tree_nodes: value?.randomise_tree_nodes,
            onChange: (data) => {
              debouncedChange({ ...value, ...data });
            }
          }}
        />
      );

    default:
      return <BlockableComponent kind={kind} componentProps={{ readOnly: true }} />;
  }
};
